import React, { useEffect, useState } from "react"
import DateTimeField from "components/Common/DateTimeField"
import { useHistory, useLocation } from "react-router-dom"
import { Alert, Button, FormGroup, Input, Label } from "reactstrap"
import { get } from "helpers/api_helper"
import "./addressFilters.scss"

// ——— What the Logistics pages that keep their filters in the address share: Sheets and Shipments ———

const REFUSED = "Check the filters: a period runs from its first day to its last, a year at most."

// The address's filters among `names`, in its own order, as the API's query.
const filterQuery =(search, names) => new URLSearchParams([...new URLSearchParams(search)].filter(([name]) => names.includes(name))).toString()

// The filter a page shows, with the period the API chose, as its export's parameters: what is set, and no default (`defaults`).
export const filterParams = (filter, names, defaults = {}) => Object.fromEntries(names
  .filter(name => filter[name] != null && filter[name] !== false && filter[name] !== defaults[name]).map(name => [name, String(filter[name])]))

// A page read from `url` on the filters its address holds (`names`, as the API reads them). The answer is `ready` only for the filter
// the address asks for, never the previous one while the next loads, which keeps the filters' options meanwhile. A refused filter is
// explained; another failure (`failed`) can be retried. The page changes its filters with `setParam` (an empty value drops one) or
// `replaceSearch`.
export const useAddressRead = (url, names, failed) => {
  const location = useLocation()
  const history = useHistory()
  const query = filterQuery(location.search, names)
  const [attempt, setAttempt] = useState(0)
  const [state, setState] = useState({})

  useEffect(() => {
    let current = true
    setState(previous => ({ answer: previous.answer }))
    get(`${url}${query ? `?${query}` : ""}`)
      .then(response => current && setState({ answer: response?.data, query }))
      .catch(error => current && setState(previous => ({ answer: previous.answer,
        error: error?.response?.data?.message === "INVALID_LOGISTICS_FILTER" ? REFUSED : failed })))
    return () => { current = false }
  }, [url, query, attempt, failed])

  const replaceSearch = next => history.replace(`${location.pathname}${next.toString() ? `?${next}` : ""}`)
  const setParam = (name, value) => {
    const next = new URLSearchParams(location.search)
    if (value === "" || value === false || value == null) next.delete(name)
    else next.set(name, value === true ? "true" : value)
    replaceSearch(next)
  }
  // `reload` reads the same filter again, for example after a command on the page.
  const reload = () => setAttempt(value => value + 1)
  return { params: new URLSearchParams(location.search), answer: state.answer, ready: Boolean(state.answer) && state.query === query, error: state.error,
    retry: state.error === failed ? reload : null, reload, setParam, replaceSearch }
}

// Why the page could not be read, with "Retry" when reading it again may work.
export const ReadError = ({ error, retry }) => (error ? <Alert color="danger">{error}{" "}
  {retry && <Button color="link" className="p-0 align-baseline" onClick={retry}>Retry</Button>}
</Alert> : null)

// A select of the filters: "All …" first, then the choices; a value the address asks for that the choices lack stays shown.
export const Choice = ({ id, label, value, all, choices, missing, onChange }) => <FormGroup className="logistics-filter mb-0">
  <Label for={id} className="small text-muted mb-1">{label}</Label>
  <Input id={id} type="select" bsSize="sm" value={value} onChange={event => onChange(event.target.value)}>
    <option value="">{all}</option>
    {value && !choices.some(choice => choice.value === value) && <option value={value}>{missing}</option>}
    {choices.map(choice => <option key={choice.value} value={choice.value}>{choice.label}</option>)}
  </Input>
</FormGroup>

// A period of days, from and to, each the address's or, without one, the API's.
export const Period = ({ id, label, from, to, onChange }) => <div role="group" aria-labelledby={id} className="logistics-period">
  <div id={id} className="small text-muted mb-1">{label}</div>
  <div className="logistics-dates">
    <DateTimeField aria-label="From" mode="date" bsSize="sm" value={from} rangeEnd={to || ""} onChange={event => onChange("from", event.target.value)} />
    <DateTimeField aria-label="To" mode="date" bsSize="sm" value={to} rangeStart={from || ""} onChange={event => onChange("to", event.target.value)} />
  </div>
</div>
